import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Plugin de pantalla completa
import "leaflet.fullscreen/Control.FullScreen.css";
import "leaflet.fullscreen";

import React from "react";
import type { IMonitorMarker } from "../interfaces";
import type { IProyecto } from "../../../modelos/Proyectos/entidades/IProyecto";
import { Box, Text, Group, Badge, Stack } from "@mantine/core";
import { IconUser, IconMapPin, IconInfoCircle, IconExternalLink, IconClock, IconAlertTriangle, IconClipboardList, IconLogin } from "@tabler/icons-react";
import dayjs from "dayjs";
import { EnumEstadoTarea, EstadoTareaColors, EstadoTareaIconos } from "../../Tareas/enums/enumEstadoTarea";

// Coordenadas por defecto (Bogotá)
const coordenadasDefecto: [number, number] = [4.711, -74.0721];

// 🔹 Helper para identificar inicio de sesión
const esInicioSesion = (tipoPunto?: string): boolean => {
  const str = (tipoPunto || "").toLowerCase().trim();
  return str === "login" || str === "ingresojornada" || str === "ingreso" || str.includes("sesión") || str.includes("jornada");
};

// 🔹 Estructura para marcadores agrupados por ubicación
interface IGroupedMonitorMarker {
  id: string;
  latitud: number;
  longitud: number;
  tecnico?: string;
  eventos: {
    orden: number;
    tipoPunto?: string;
    fecha?: string;
    titulo: string;
    esInicioSesion?: boolean;
    servicioId?: number;
    estadoTareaId?: number;
    estadoTareaNombre?: string;
  }[];
}

// 🔹 Función para formatear el Tipo de Punto de forma amigable en español (Mapeo de Enum)
const mapearTipoPunto = (tipo?: string): string => {
  if (esInicioSesion(tipo)) {
    return "Inicio de Sesión";
  }
  const str = (tipo || "").toLowerCase().trim();
  if (str === "llegadapunto" || str === "llegada") {
    return "Llegada a Punto";
  }
  if (str === "iniciotarea" || str === "inicio" || str === "iniciotrabajo" || str.includes("actividad") || str.includes("tarea")) {
    return "Inicio de Actividad";
  }
  if (str === "finalizado" || str === "fin" || str === "terminado") {
    return "Finalizado";
  }
  
  // Separar palabras pegadas y capitalizar
  return (tipo || "Ubicación")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

// 🔹 Función para agrupar marcadores por sus coordenadas (tolerancia de ~110m para unificar eventos cercanos)
const agruparMarcadoresPorCoordenadas = (lista: IMonitorMarker[]): IGroupedMonitorMarker[] => {
  const grupos: Record<string, IGroupedMonitorMarker> = {};
  
  lista.forEach((marker) => {
    // Redondear a 3 decimales (~110m de tolerancia) para consolidar eventos del mismo punto de visita
    const clave = `${marker.latitud.toFixed(3)},${marker.longitud.toFixed(3)}`;
    
    if (!grupos[clave]) {
      grupos[clave] = {
        id: clave,
        latitud: marker.latitud,
        longitud: marker.longitud,
        tecnico: marker.tecnico,
        eventos: []
      };
    }
    
    grupos[clave].eventos.push({
      orden: marker.orden,
      tipoPunto: marker.tipoPunto,
      fecha: marker.fecha,
      titulo: marker.titulo,
      esInicioSesion: marker.esInicioSesion,
      servicioId: marker.servicioId,
      estadoTareaId: marker.estadoTareaId,
      estadoTareaNombre: marker.estadoTareaNombre
    });
  });
  
  const resultado = Object.values(grupos);
  
  // Ordenar los eventos dentro de cada grupo por fecha, de la más reciente a la antigua
  resultado.forEach((grupo) => {
    grupo.eventos.sort((a, b) => {
      const fechaA = a.fecha ? dayjs(a.fecha).valueOf() : 0;
      const fechaB = b.fecha ? dayjs(b.fecha).valueOf() : 0;
      return fechaB - fechaA; // Descendente (más reciente primero)
    });
  });
  
  return resultado;
};

// 🔹 Obtener el color principal para el marcador del técnico (Unificado a Verde Distintivo)
const obtenerColorGrupo = (_eventos?: { tipoPunto?: string }[]): string => {
  return "#2b8a3e"; // Verde unificado para todos los pines de técnicos
};

// 🔹 Obtener estilos CSS inline para el Badge con colores vivos de alto contraste (formato mockups)
const getBadgeStyle = (tipoPunto?: string) => {
  let color = "#0074d9"; // Azul de inicio de actividad por defecto
  
  if (esInicioSesion(tipoPunto)) {
    color = "#2b8a3e"; // Verde
  } else {
    const tipo = (tipoPunto || "").toLowerCase();
    if (tipo.includes("llegada") || tipo === "llegadapunto") {
      color = "#e67e22"; // Naranja
    } else if (tipo.includes("finalizado") || tipo === "finalizado" || tipo === "fin" || tipo === "terminado") {
      color = "#b13e0f"; // Café
    }
  }

  return {
    backgroundColor: color,
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "8px",
    height: "16px",
    border: "none",
    textTransform: "uppercase" as const,
    paddingLeft: "6px",
    paddingRight: "6px",
    borderRadius: "4px",
    lineHeight: "15px",
    margin: 0
  };
};

// 🔹 Obtener estilos CSS inline para el Badge de Alertas (>40 min) redondo (píldora) con color HEX explícito
const getAlertBadgeStyle = () => {
  return {
    backgroundColor: "#e03131", // Color HEX Rojo de Alerta
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "9px",
    height: "18px",
    border: "none",
    textTransform: "uppercase" as const,
    paddingLeft: "8px",
    paddingRight: "8px",
    borderRadius: "100px", // Totalmente redondo / píldora
    lineHeight: "18px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0, // Evita que se encoja o genere elipsis (+40...)
    whiteSpace: "nowrap" as const,
    overflow: "visible",
    margin: 0,
    marginLeft: "6px"
  };
};

// 🔹 Función para crear el pin marcador personalizado de TÉCNICO (con ícono de usuario distintivo)
const createCustomIcon = (color: string, masDeUnEvento: boolean) => {
  // Si hay más de un evento en este mismo punto, se dibuja un pequeño punto rojo indicador arriba a la derecha
  const indicadorMultiples = masDeUnEvento 
    ? `<circle cx="22" cy="5" r="4" fill="#fa5252" stroke="white" stroke-width="1"/>`
    : "";

  const pinSvg = `
    <svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Sombra del pin técnico -->
      <ellipse cx="14" cy="33" rx="6.5" ry="2" fill="black" fill-opacity="0.25"/>
      
      <!-- Pin principal -->
      <path d="M14 0C6.27 0 0 6.27 0 14C0 23.5 12.9 34.4 13.4 34.9C13.7 35.2 14.3 35.2 14.6 34.9C15.1 34.4 28 23.5 28 14C28 6.27 21.73 0 14 0Z" fill="${color}"/>
      
      <!-- Círculo interior blanco -->
      <circle cx="14" cy="14" r="8.5" fill="white"/>
      
      <!-- Ícono de persona / técnico en el centro -->
      <path d="M14 13.5C15.38 13.5 16.5 12.38 16.5 11C16.5 9.62 15.38 8.5 14 8.5C12.62 8.5 11.5 9.62 11.5 11C11.5 12.38 12.62 13.5 14 13.5ZM14 15C12.0034 15 8 16.005 8 18V19.5H20V18C20 16.005 15.9966 15 14 15Z" fill="${color}"/>
      
      <!-- Indicador de eventos apilados -->
      ${indicadorMultiples}
    </svg>
  `;
  
  return L.divIcon({
    html: pinSvg,
    className: "custom-leaflet-marker",
    iconSize: [28, 36],
    iconAnchor: [14, 35],
    popupAnchor: [0, -34],
  });
};

// 🔹 Crear ícono personalizado para proyectos (Edificio)
const createProjectIcon = () => {
  const buildingSvg = `
    <svg width="26" height="34" viewBox="0 0 26 34" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Sombra del pin -->
      <ellipse cx="13" cy="31" rx="6" ry="2" fill="black" fill-opacity="0.2"/>
      
      <!-- Pin principal color primario distintivo -->
      <path d="M13 0C5.83 0 0 5.83 0 13C0 21.8 12 32.8 12.5 33.3C12.8 33.6 13.2 33.6 13.5 33.3C14 32.8 26 21.8 26 13C26 5.83 20.17 0 13 0Z" fill="#0074d9"/>
      
      <!-- Círculo interior blanco (estético) -->
      <circle cx="13" cy="13" r="8" fill="white"/>
      
      <!-- Ícono de edificio adentro del círculo -->
      <path d="M9 18V8H17V18H15V14H11V18H9ZM10 9H12V11H10V9ZM14 9H16V11H14V9ZM10 12H12V14H10V12ZM14 12H16V14H14V12Z" fill="#0074d9"/>
    </svg>
  `;
  
  return L.divIcon({
    html: buildingSvg,
    className: "custom-leaflet-marker",
    iconSize: [26, 34],
    iconAnchor: [13, 33],
    popupAnchor: [0, -32],
  });
};

// 🔹 Verificar si han transcurrido más de 40 minutos (Alerta)
const esAlertaTiempo = (fechaIso?: string, ahoraObj?: dayjs.Dayjs): boolean => {
  if (!fechaIso) return false;
  const ahora = ahoraObj || dayjs();
  const fechaEvento = dayjs(fechaIso);
  const diffMinutos = ahora.diff(fechaEvento, "minute");
  return diffMinutos > 40;
};

// 🔹 Calcular tiempo transcurrido desde un reporte/evento en formato amigable con segundos
const calcularTiempoTranscurrido = (fechaIso?: string, ahoraObj?: dayjs.Dayjs): string => {
  if (!fechaIso) return "N/D";
  const ahora = ahoraObj || dayjs();
  const fechaEvento = dayjs(fechaIso);
  const diffSegundos = ahora.diff(fechaEvento, "second");
  
  if (diffSegundos <= 0) return "0 segundos";
  
  if (diffSegundos < 60) {
    return `${diffSegundos} ${diffSegundos === 1 ? 'segundo' : 'segundos'}`;
  }
  
  const horas = Math.floor(diffSegundos / 3600);
  const minutos = Math.floor((diffSegundos % 3600) / 60);
  const segundos = diffSegundos % 60;
  
  if (horas === 0) {
    const segTxt = segundos > 0 ? ` y ${segundos} ${segundos === 1 ? 'segundo' : 'segundos'}` : '';
    return `${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}${segTxt}`;
  }
  
  const minTxt = minutos > 0 ? ` ${minutos} min` : '';
  const segTxt = segundos > 0 ? ` ${segundos} seg` : '';
  return `${horas} ${horas === 1 ? 'hora' : 'horas'}${minTxt}${segTxt}`;
};

// 🔹 Control de pantalla completa
const FullscreenControl = () => {
  const map = useMap();

  React.useEffect(() => {
    if (!(map as any).fullscreenControl) {
      L.control.fullscreen({
        position: "topleft",
        title: "Pantalla completa",
        titleCancel: "Salir de pantalla completa",
      }).addTo(map);
    }
  }, [map]);

  return null;
};

// 🔹 Componente para ajustar dinámicamente el mapa para mostrar todos los marcadores
// Se incrementa el maxZoom a 19 para permitir que el usuario visualice con detalle máximo los puntos cercanos
const FitBounds = ({ markers }: { markers: IMonitorMarker[] }) => {
  const map = useMap();

  React.useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.latitud, m.longitud]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 19 });
    } else {
      map.setView(coordenadasDefecto, 12);
    }
  }, [map, markers]);

  return null;
};

interface IMapaMonitorVistaProps {
  markers: IMonitorMarker[];
  proyectos: IProyecto[];
  height?: string;
}

export const MapaMonitorVista: React.FC<IMapaMonitorVistaProps> = ({
  markers,
  proyectos = [],
  height = "600px",
}) => {
  // 🔹 Estado para actualizar en tiempo real (segundo a segundo) el tiempo transcurrido
  const [ahora, setAhora] = React.useState(() => dayjs());

  React.useEffect(() => {
    const timer = setInterval(() => {
      setAhora(dayjs());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Agrupar los marcadores por coordenadas con tolerancia para consolidar eventos traslapados
  const marcadoresAgrupados = agruparMarcadoresPorCoordenadas(markers);

  return (
    <Box style={{ position: "relative", width: "100%", height, borderRadius: "8px", overflow: "hidden" }}>
      <style>{`
        /* Sobrescribir popups de Leaflet para darles diseño premium */
        .leaflet-popup-content-wrapper {
          border-radius: 12px !important;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08) !important;
          padding: 0 !important;
          overflow: hidden;
          background: #ffffff !important;
        }
        .leaflet-popup-content {
          margin: 0 !important;
          width: 285px !important;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
        }
        .leaflet-popup-tip {
          background: white !important;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08) !important;
        }
        .leaflet-popup-close-button {
          color: #adb5bd !important;
          padding: 6px 10px 0 0 !important;
          font-size: 15px !important;
          z-index: 1001 !important;
        }
        .leaflet-popup-close-button:hover {
          color: #495057 !important;
          background: transparent !important;
        }
        /* Efecto hover interactivo en el SVG hijo, respetando la posición 3D de Leaflet */
        .custom-leaflet-marker svg {
          transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
        }
        .custom-leaflet-marker:hover svg {
          transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          transform: translateY(-4px) scale(1.1);
          filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.25));
        }
      `}</style>

      <MapContainer
        center={coordenadasDefecto}
        zoom={12}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${import.meta.env.VITE_CARTO_API_KEY}`}
        />

        {marcadoresAgrupados.map((grupo) => {
          const colorPin = obtenerColorGrupo(grupo.eventos);
          const primerEvento = grupo.eventos[0];
          const masDeUnEvento = grupo.eventos.length > 1;

          return (
            <Marker 
              key={grupo.id} 
              position={[grupo.latitud, grupo.longitud]}
              icon={createCustomIcon(colorPin, masDeUnEvento)}
            >
              <Popup offset={L.point(0, 8)}>
                <Box style={{ width: "285px" }}>
                  {/* Cabecera de la Tarjeta (Compacta y sin rellenos excesivos) */}
                  <Box style={{ background: "#ffffff", padding: "8px 12px 6px 12px" }}>
                    {/* Fila del Badge (se mantiene espacio libre a la derecha para no cruzar con el boton X) */}
                    <Box style={{ paddingRight: "24px", margin: 0 }}>
                      <Badge size="xs" style={{ backgroundColor: "#2b8a3e", color: "#ffffff", fontWeight: 700, fontSize: "8px", height: "16px", textTransform: "uppercase", border: "none", borderRadius: "4px", margin: 0 }}>
                        ÚLTIMA UBICACIÓN REPORTADA
                      </Badge>
                    </Box>
                    
                    {/* Título o Fecha y Hora (margin reseteado para evitar estilos globales de Leaflet) */}
                    {masDeUnEvento ? (
                      <Text fw={700} size="xs" style={{ color: "#212529", lineHeight: 1.1, margin: 0, marginTop: "6px" }}>
                        Actividades registradas ({grupo.eventos.length})
                      </Text>
                    ) : (
                      <Text size="xs" color="#495057" fw={600} style={{ lineHeight: 1.1, margin: 0, marginTop: "6px" }}>
                        {primerEvento.fecha ? dayjs(primerEvento.fecha).format("DD/MM/YYYY hh:mm:ss A") : "N/D"}
                      </Text>
                    )}

                    {/* Fila de Coordenadas con icono y enlace (margin reseteado) */}
                    <Group gap={4} align="center" style={{ margin: 0, marginTop: "6px" }}>
                      <IconMapPin size={12} color="#0074d9" />
                      <Text size="xs" color="#868e96" style={{ fontFamily: "monospace", display: "inline-flex", alignItems: "center", lineHeight: 1, margin: 0 }}>
                        {grupo.latitud.toFixed(6)}, {grupo.longitud.toFixed(6)}
                      </Text>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${grupo.latitud},${grupo.longitud}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", color: "#0074d9", margin: 0 }}
                      >
                        <IconExternalLink size={10} />
                      </a>
                    </Group>
                  </Box>

                  {/* Cuerpo de la Tarjeta */}
                  {masDeUnEvento ? (
                    /* Vista cuando hay Múltiples Puntos/Eventos en la misma coordenada (Compacto y con espaciados del mockup) */
                    <Box style={{ maxHeight: "250px", overflowY: "auto", borderTop: "1px solid #f1f3f5" }}>
                      {grupo.eventos.map((ev, index) => {
                        const alertaEv = esAlertaTiempo(ev.fecha, ahora);
                        const esLoginEv = esInicioSesion(ev.tipoPunto);
                        return (
                          <Box 
                            key={index} 
                            style={{ 
                              borderBottom: index < grupo.eventos.length - 1 ? "1px solid #f1f3f5" : "none", 
                              padding: "6px 12px"
                            }}
                          >
                            <Group justify="space-between" align="center" wrap="nowrap" style={{ margin: 0, marginBottom: "4px" }}>
                              <Badge size="xs" style={getBadgeStyle(ev.tipoPunto)}>
                                {mapearTipoPunto(ev.tipoPunto)}
                              </Badge>
                              <Text size="9px" color="#868e96" style={{ lineHeight: 1, margin: 0 }}>
                                {ev.fecha ? dayjs(ev.fecha).format("DD/MM/YYYY hh:mm:ss A") : ""}
                              </Text>
                            </Group>
                            
                            {!esLoginEv && (
                              <Text fw={700} size="xs" style={{ color: "#212529", margin: 0, marginTop: "2px", display: "block", lineHeight: 1.1 }}>
                                {ev.titulo || `Tarea #${ev.orden}`}
                              </Text>
                            )}

                            {ev.fecha && (
                              <Group gap={4} align="center" style={{ margin: 0, marginTop: "4px" }}>
                                {alertaEv ? (
                                  <IconAlertTriangle size={11} color="#e03131" />
                                ) : (
                                  <IconClock size={10} color="#868e96" />
                                )}
                                <Text size="10px" color={alertaEv ? "#e03131" : "#495057"} fw={alertaEv ? 700 : 400} style={{ lineHeight: 1, margin: 0 }}>
                                  <Text span fw={700} color={alertaEv ? "#e03131" : "dimmed"} size="8px" style={{ textTransform: "uppercase", marginRight: 4 }}>Transcurrido:</Text>
                                  {calcularTiempoTranscurrido(ev.fecha, ahora)}
                                </Text>
                                {alertaEv && (
                                  <Badge size="xs" style={{ ...getAlertBadgeStyle(), fontSize: "7px", height: "13px", paddingLeft: "4px", paddingRight: "4px" }}>
                                    +40 MIN
                                  </Badge>
                                )}
                              </Group>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    /* Vista para un único Punto/Evento (Compacta y de baja altura) */
                    <Box style={{ borderTop: "1px solid #f1f3f5", padding: "6px 12px" }}>
                      <Stack gap={4}>
                        {grupo.tecnico && (
                          <Group gap={6} wrap="nowrap" align="center" style={{ margin: 0 }}>
                            <IconUser size={14} color="#495057" style={{ flexShrink: 0 }} />
                            <Text size="xs" color="#212529" fw={600} style={{ lineHeight: 1.2, margin: 0 }}>
                              {grupo.tecnico}
                            </Text>
                          </Group>
                        )}

                        {primerEvento.esInicioSesion || esInicioSesion(primerEvento.tipoPunto) ? (
                          <Group gap={6} wrap="nowrap" align="center" style={{ margin: 0 }}>
                            <Badge 
                              size="xs" 
                              rightSection={<IconLogin size={12} color="#0074d9" style={{ display: "block" }} />}
                              style={{ 
                                backgroundColor: "#e8f4ff", 
                                color: "#0074d9", 
                                border: "1px solid #cce5ff",
                                fontWeight: 600, 
                                fontSize: "10px", 
                                height: "20px", 
                                borderRadius: "100px", 
                                paddingLeft: "10px", 
                                paddingRight: "10px", 
                                textTransform: "none", 
                                margin: 0 
                              }}
                              styles={{
                                section: {
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginLeft: "5px"
                                },
                                label: {
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  lineHeight: 1
                                }
                              }}
                            >
                              Inicio de Sesión
                            </Badge>
                          </Group>
                        ) : (
                          <Group gap={6} wrap="nowrap" align="center" style={{ margin: 0 }}>
                            <Group gap={6} wrap="nowrap" align="center" style={{ margin: 0 }}>
                              <IconClipboardList size={14} color="#495057" style={{ flexShrink: 0 }} />
                              <Text size="xs" color="#212529" fw={600} style={{ lineHeight: 1.2, margin: 0 }}>
                                Tarea #{primerEvento.servicioId || "N/D"}
                              </Text>
                            </Group>

                            <Text size="xs" color="#868e96" fw={400} style={{ margin: "0 2px", lineHeight: 1 }}>-</Text>

                            {primerEvento.estadoTareaNombre && (() => {
                              const IconoEstado = primerEvento.estadoTareaId ? EstadoTareaIconos[primerEvento.estadoTareaId as EnumEstadoTarea] : null;
                              const colorEstado = primerEvento.estadoTareaId ? (EstadoTareaColors[primerEvento.estadoTareaId as EnumEstadoTarea] || "#0074d9") : "#0074d9";
                              return (
                                <Badge 
                                  size="xs" 
                                  leftSection={IconoEstado ? <IconoEstado size={11} color="#ffffff" style={{ display: "block" }} /> : null}
                                  style={{ 
                                    backgroundColor: colorEstado, 
                                    color: "#ffffff", 
                                    fontWeight: 700, 
                                    fontSize: "9px", 
                                    height: "18px", 
                                    border: "none", 
                                    borderRadius: "100px", 
                                    paddingLeft: "7px", 
                                    paddingRight: "7px", 
                                    textTransform: "capitalize",
                                    margin: 0 
                                  }}
                                  styles={{
                                    section: {
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      marginRight: "4px"
                                    },
                                    label: {
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      lineHeight: 1
                                    }
                                  }}
                                >
                                  {primerEvento.estadoTareaNombre}
                                </Badge>
                              );
                            })()}
                          </Group>
                        )}

                        {primerEvento.fecha && (() => {
                          const alerta = esAlertaTiempo(primerEvento.fecha, ahora);
                          return (
                            <Group justify="space-between" align="center" wrap="nowrap" style={{ margin: 0, marginTop: "2px" }}>
                              <Group gap={6} wrap="nowrap" align="center" style={{ flex: 1, minWidth: 0 }}>
                                {alerta ? (
                                  <IconAlertTriangle size={14} color="#e03131" style={{ flexShrink: 0 }} />
                                ) : (
                                  <IconClock size={12} color="#adb5bd" style={{ flexShrink: 0 }} />
                                )}
                                <Text size="xs" color={alerta ? "#e03131" : "#495057"} fw={alerta ? 700 : 400} style={{ lineHeight: 1.2, margin: 0 }}>
                                  <Text span fw={700} color={alerta ? "#e03131" : "dimmed"} size="9px" style={{ textTransform: "uppercase", marginRight: 4, display: "inline-block" }}>
                                    Tiempo transcurrido:
                                  </Text>
                                  {calcularTiempoTranscurrido(primerEvento.fecha, ahora)}
                                </Text>
                              </Group>

                              {alerta && (
                                <Badge size="xs" style={getAlertBadgeStyle()}>
                                  +40 MIN
                                </Badge>
                              )}
                            </Group>
                          );
                        })()}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Popup>
            </Marker>
          );
        })}

        {/* Renderizar los proyectos en el mapa */}
        {proyectos.map((proyecto) => {
          const lat = typeof proyecto.Latitud === "number" ? proyecto.Latitud : parseFloat(String(proyecto.Latitud || "").trim());
          const lng = typeof proyecto.Longitud === "number" ? proyecto.Longitud : parseFloat(String(proyecto.Longitud || "").trim());
          
          if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) {
            return null;
          }
          
          return (
            <Marker
              key={`proyecto-${proyecto.Id}`}
              position={[lat, lng]}
              icon={createProjectIcon()}
            >
              <Popup offset={L.point(0, 8)}>
                <Box style={{ width: "270px" }}>
                  {/* Cabecera del popup de Proyecto */}
                  <Box style={{ background: "#ffffff", padding: "8px 12px 6px 12px" }}>
                    <Box style={{ paddingRight: "24px", margin: 0 }}>
                      <Badge size="xs" style={{ backgroundColor: "#0074d9", color: "#ffffff", fontWeight: 700, fontSize: "8px", height: "16px", textTransform: "uppercase", border: "none", borderRadius: "4px", margin: 0 }}>
                        Proyecto
                      </Badge>
                    </Box>
                    
                    <Text fw={700} size="xs" style={{ color: "#212529", lineHeight: 1.1, margin: 0, marginTop: "6px" }}>
                      {proyecto.Nombre}
                    </Text>

                    {/* Fila de Coordenadas con icono y enlace */}
                    <Group gap={4} align="center" style={{ margin: 0, marginTop: "6px" }}>
                      <IconMapPin size={12} color="#0074d9" />
                      <Text size="xs" color="#868e96" style={{ fontFamily: "monospace", display: "inline-flex", alignItems: "center", lineHeight: 1, margin: 0 }}>
                        {lat.toFixed(6)}, {lng.toFixed(6)}
                      </Text>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", color: "#0074d9", margin: 0 }}
                      >
                        <IconExternalLink size={10} />
                      </a>
                    </Group>
                  </Box>

                  {/* Cuerpo del popup de Proyecto */}
                  <Box style={{ borderTop: "1px solid #f1f3f5", padding: "6px 12px" }}>
                    <Stack gap={4}>
                      {proyecto.Direccion && (
                        <Group gap="xs" wrap="nowrap" align="center" style={{ margin: 0 }}>
                          <IconInfoCircle size={12} color="#adb5bd" style={{ flexShrink: 0 }} />
                          <Text size="xs" color="#495057" style={{ lineHeight: 1.1, margin: 0 }}>
                            <Text span fw={700} color="dimmed" size="9px" style={{ textTransform: "uppercase", marginRight: 4 }}>Dirección:</Text>
                            {proyecto.Direccion}
                          </Text>
                        </Group>
                      )}
                      
                    </Stack>
                  </Box>
                </Box>
              </Popup>
            </Marker>
          );
        })}

        {/* Ajusta el encuadre automáticamente */}
        <FitBounds markers={markers} />

        <FullscreenControl />
      </MapContainer>
    </Box>
  );
};
